/**
 * #/items LEE LOS NODOS DE LA BASE (SALV-T9 parte 2, Ares #20511).
 *
 * Hasta hoy #/items partia de ITEM_CATALOG (50 fijos en Item.js): se veian 47 de 350 nodos y ninguno decia quien lo
 * verifico. La API ya tiene la puerta con TODOS (SALV-T8, nexo-api 374): GET /api/v1/Projects/items devuelve cada nodo
 * con su project_key y una LECTURA CON RESPALDO (filePath, route, status, note, version, verificadoPor, verificadoAt,
 * owner) leida de las claves que cada quien escribio. Aqui se traduce esa forma a la fila que pinta la vista.
 *
 * Puro: sin DOM. Lo mide scripts/check-items-desde-api.mjs.
 *
 * @module domain/nodosDelCatalogo
 */

import { selloDeVerificacion, duenoDelNodo } from './verificacionNodo.js';

/** Los tipos de project_items (la columna item_type), con el nombre que se lee. */
export const TIPOS_NODO = Object.freeze({
  instruction:    { label: 'Instruccion',     color: '#79c0ff', icon: '☰' },
  form:           { label: 'Formulario',      color: '#e3b341', icon: '▤' },
  endpoint:       { label: 'Endpoint',        color: '#3fb950', icon: '⇄' },
  view:           { label: 'Vista',           color: '#58a6ff', icon: '▣' },
  capability:     { label: 'Capacidad',       color: '#d2a8ff', icon: '★' },
  workflow:       { label: 'Flujo n8n',       color: '#f97583', icon: '⚡' },
  component:      { label: 'Componente',      color: '#a371f7', icon: '⬢' },
  infrastructure: { label: 'Infraestructura', color: '#d29922', icon: '⚙' },
  table:          { label: 'Tabla',           color: '#56d4dd', icon: '▦' }
});

const texto = (v) => (typeof v === 'string' ? v.trim() : '');

function parsear(metadata) {
  if (metadata && typeof metadata === 'object') return metadata;
  if (typeof metadata !== 'string' || !metadata.trim()) return {};
  try { const m = JSON.parse(metadata); return m && typeof m === 'object' ? m : {}; } catch { return {}; }
}

/**
 * El estado de la lectura, a los tres puntos que pinta la tabla (activo / inactivo / borrador). El texto original viaja
 * aparte (estado) para no perder lo que dijo el nodo.
 */
export function estadoDelNodo(status) {
  const s = texto(status).toLowerCase();
  if (!s || s === 'active' || s === 'activo' || s === 'vive') return 'active';
  if (['deprecated', 'retirado', 'no_vive', 'inactive', 'inactivo', 'archivado'].includes(s)) return 'inactive';
  if (['borrador', 'propuesta', 'maqueta', 'pr_abierto', 'draft'].includes(s)) return 'draft';
  return 'active';
}

/**
 * Un nodo de GET /Projects/items -> la fila de #/items. Tolera camelCase y PascalCase (F2 del molde: el costo es una
 * linea). El sello sale de la LECTURA (verificadoPor con respaldo); si la API no la trae, de la metadata, como en
 * #/projects/:key.
 */
export function nodoAItem(dto) {
  const d = dto && typeof dto === 'object' ? dto : {};
  const meta = parsear(d.metadata ?? d.Metadata);
  const l = d.lectura ?? d.Lectura ?? {};
  const deLectura = (k) => texto(l[k] ?? l[k.charAt(0).toUpperCase() + k.slice(1)]);
  const ref = texto(d.itemRef ?? d.ItemRef);
  const sello = selloDeVerificacion({
    verificado_por: deLectura('verificadoPor') || meta.verificado_por,
    verificado_at: deLectura('verificadoAt') || meta.verificado_at,
    sin_verificar: meta.sin_verificar
  });
  const estado = deLectura('status');
  return {
    id: ref,
    name: texto(meta.nombre) || texto(meta.name) || texto(meta.title) || ref,
    type: texto(d.itemType ?? d.ItemType) || 'component',
    version: deLectura('version') || texto(meta.version),
    status: estadoDelNodo(estado),
    estado,
    description: deLectura('note') || texto(meta.description) || texto(meta.descripcion),
    externalId: texto(meta.external_id) || null,
    usedBy: [],
    metadata: meta,
    _relation: texto(d.relation ?? d.Relation) || 'uses',
    _projectKey: texto(d.projectKey ?? d.ProjectKey),
    _whereLives: deLectura('filePath') || deLectura('route') || texto(meta.table) || texto(meta.external_id),
    _sello: sello,
    _dueno: deLectura('owner') || duenoDelNodo(meta)
  };
}

/**
 * La respuesta del cliente (ApiBase: {ok, data, code, error}) -> {ok, items} o {ok:false, code, error}.
 * Una respuesta sin la lista NO es un catalogo vacio: es que no la entendimos, y se dice.
 */
export function itemsDesdeRespuesta(res) {
  if (!res || !res.ok) return { ok: false, code: res?.code ?? 'NETWORK', error: res?.error || 'sin respuesta' };
  const cuerpo = res.data;
  const lista = Array.isArray(cuerpo) ? cuerpo
    : Array.isArray(cuerpo?.data) ? cuerpo.data
    : Array.isArray(cuerpo?.Data) ? cuerpo.Data
    : Array.isArray(cuerpo?.data?.items) ? cuerpo.data.items
    : null;
  if (!lista) return { ok: false, code: 'FORMA', error: 'la respuesta no trae la lista de nodos' };
  return { ok: true, items: lista.map(nodoAItem).filter((i) => i.id) };
}

/** Las filas que se ven con los filtros de ahora (en modo api). Pura; la mide la guardia. */
export function filtrarNodos(items, { tipo = 'all', categoria = 'all', proyecto = 'all', sinVerificar = false } = {}) {
  return items.filter(i => (tipo === 'all' || i.type === tipo)
    && (categoria === 'all' || i.metadata?.category === categoria)
    && (proyecto === 'all' || i._projectKey === proyecto)
    && (!sinVerificar || (i.status !== 'inactive' && !i._sello?.verificado)));
}
